import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminTrilhaService } from '../../shared/services';
import type { Trilha } from '../../shared/types';
import { 
  MapPin, 
  Plus, 
  Search, 
  Edit2, 
  Trash2, 
  CheckCircle, 
  XCircle,
  BookOpen
} from 'lucide-react';
import { Link } from 'react-router-dom';

export function AdminTrilhasPage() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [ativaFilter, setAtivaFilter] = useState<string>('');

  const { data, isLoading } = useQuery({
    queryKey: ['admin-trilhas', search, ativaFilter],
    queryFn: () => adminTrilhaService.getTrilhas({ 
      search: search || undefined, 
      ativa: ativaFilter === '' ? undefined : ativaFilter === 'true'
    }),
  });

  const deleteMutation = useMutation({
    mutationFn: (trilhaId: string) => adminTrilhaService.deleteTrilha(trilhaId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-trilhas'] });
    },
  });

  const handleDelete = (trilhaId: string, trilhaTitulo: string) => {
    if (window.confirm(`Tem certeza que deseja excluir a trilha "${trilhaTitulo}"?`)) {
      deleteMutation.mutate(trilhaId);
    }
  };

  return (
    <div className="p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <MapPin className="w-8 h-8 text-blue-600" />
              Gerenciar Trilhas
            </h1>
            <p className="text-gray-600 mt-2">
              Crie e gerencie trilhas de aprendizado da plataforma
            </p>
          </div>
          <Link
            to="/admin/trilhas/nova"
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-5 h-5" />
            Nova Trilha
          </Link>
        </div>

        {/* Filtros */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar trilhas..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <select
              value={ativaFilter}
              onChange={(e) => setAtivaFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Todos os status</option>
              <option value="true">Ativas</option>
              <option value="false">Inativas</option>
            </select>
          </div>
        </div>

        {/* Estatísticas */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total de Trilhas</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{data?.length || 0}</p>
              </div>
              <MapPin className="w-10 h-10 text-blue-600 opacity-50" />
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Trilhas Ativas</p>
                <p className="text-2xl font-bold text-green-900 mt-1">
                  {data?.filter((t: Trilha) => t.ativo).length || 0}
                </p>
              </div>
              <CheckCircle className="w-10 h-10 text-green-600 opacity-50" />
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total de Cursos</p>
                <p className="text-2xl font-bold text-blue-900 mt-1">
                  {data?.reduce((acc: number, t: Trilha) => acc + (t.cursos?.length || 0), 0) || 0}
                </p>
              </div>
              <BookOpen className="w-10 h-10 text-blue-600 opacity-50" />
            </div>
          </div>
        </div>

        {/* Grid de Trilhas */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          {isLoading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="text-gray-600 mt-4">Carregando trilhas...</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-6 p-6">
                {data?.map((trilha: Trilha) => (
                  <div
                    key={trilha.id}
                    className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow"
                  >
                    <div className="flex gap-6">
                      {/* Imagem */}
                      <img
                        src={trilha.capaUrl}
                        alt={trilha.nome}
                        className="w-48 h-32 object-cover rounded-lg"
                      />

                      {/* Conteúdo */}
                      <div className="flex-1">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <h3 className="text-xl font-bold text-gray-900 mb-2">
                              {trilha.nome}
                            </h3>
                            <p className="text-gray-600 line-clamp-2 mb-3">
                              {trilha.descricao}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Link
                              to={`/admin/trilhas/${trilha.id}/editar`}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                              title="Editar"
                            >
                              <Edit2 className="w-5 h-5" />
                            </Link>
                            <button
                              onClick={() => handleDelete(trilha.id, trilha.nome)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Excluir"
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          </div>
                        </div>

                        {/* Badges e Info */}
                        <div className="flex flex-wrap items-center gap-3">
                          {trilha.ativo ? (
                            <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
                              <CheckCircle className="w-3 h-3" />
                              Ativa
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-3 py-1 bg-red-100 text-red-800 rounded-full text-xs font-medium">
                              <XCircle className="w-3 h-3" />
                              Inativa
                            </span>
                          )}

                          <span className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                            <BookOpen className="w-3 h-3" />
                            {trilha.totalCursos || trilha.cursos?.length || 0} cursos
                          </span>

                          <span className="text-xs text-gray-500">
                            Duração: {trilha.duracaoTotalMinutos} min
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                {data && data.length === 0 && (
                  <div className="text-center py-12">
                    <MapPin className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500 text-lg">Nenhuma trilha encontrada</p>
                  </div>
                )}
              </div>

              {/* Paginação - Comentada pois a API retorna array direto */}
              {/* Paginação - Comentada pois a API retorna array direto */}
              {/*data && data.totalPages > 1 && (
                <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                  <p className="text-sm text-gray-600">
                    Mostrando {((page - 1) * data.pageSize) + 1} até {Math.min(page * data.pageSize, data.total)} de {data.total} trilhas
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Anterior
                    </button>
                    <button
                      onClick={() => setPage(p => Math.min(data.totalPages, p + 1))}
                      disabled={page === data.totalPages}
                      className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Próxima
                    </button>
                  </div>
                </div>
              )*/}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
